import React from 'react';
import { AdminListUser } from '../../types/adminUser';
import { formatDate } from '../../utils/date';

type StepCol = {
  key: string;
  label: string;
  done: (user: AdminListUser) => boolean;
};

function pageDone(user: AdminListUser, index: number): boolean {
  return Boolean(user.startupPages?.[index]);
}

function funnelClass(status: AdminListUser['funnelStatus']): string {
  if (status === 'Paid') return 'text-emerald-400';
  if (status === 'Code') return 'text-blue-300';
  return 'text-amber-400';
}

function Check({ done }: { done: boolean }) {
  if (!done) {
    return <span className="text-gray-600">—</span>;
  }
  return (
    <svg
      className="w-4 h-4 text-emerald-400 inline-block"
      fill="currentColor"
      viewBox="0 0 20 20"
      aria-label="Complete"
    >
      <path
        fillRule="evenodd"
        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
        clipRule="evenodd"
      />
    </svg>
  );
}

const INVESTOR_STEPS: StepCol[] = [
  { key: 'pricing', label: 'Pricing page', done: user => user.reachedPricingPage },
  { key: 'stripe', label: 'Stripe page', done: user => user.reachedStripePage },
  { key: 'password', label: 'Password set', done: user => user.passwordSet },
  { key: 'verified', label: 'Email verified', done: user => user.emailVerified },
  { key: 'express', label: 'Express signup', done: user => user.expressSignup },
  { key: 'company', label: 'Company', done: user => pageDone(user, 0) },
  { key: 'industry', label: 'Industry', done: user => pageDone(user, 1) },
  { key: 'team', label: 'Team prefs', done: user => pageDone(user, 2) },
  { key: 'ownership', label: 'Ownership', done: user => pageDone(user, 3) },
  { key: 'expertise', label: 'Expertise', done: user => pageDone(user, 4) },
  { key: 'matching', label: 'Matching', done: user => pageDone(user, 5) || user.profileCompleted },
];

const STARTUP_STEPS: StepCol[] = [
  { key: 'pricing', label: 'Pricing page', done: user => user.reachedPricingPage },
  { key: 'stripe', label: 'Stripe page', done: user => user.reachedStripePage },
  { key: 'password', label: 'Password set', done: user => user.passwordSet },
  { key: 'verified', label: 'Email verified', done: user => user.emailVerified },
  { key: 'page1', label: 'Page 1', done: user => pageDone(user, 0) },
  { key: 'page2', label: 'Page 2', done: user => pageDone(user, 1) },
  { key: 'page3', label: 'Page 3', done: user => pageDone(user, 2) },
  { key: 'page4', label: 'Page 4', done: user => pageDone(user, 3) },
  { key: 'page5', label: 'Page 5', done: user => pageDone(user, 4) },
  { key: 'matching', label: 'Matching', done: user => pageDone(user, 5) || user.profileCompleted },
];

const thClass =
  'sticky top-0 z-20 bg-gray-800 px-3 py-2 text-[10px] font-medium text-gray-400 uppercase tracking-wider whitespace-nowrap border-b border-gray-700';
const tdClass = 'px-3 py-2 text-xs text-gray-300 whitespace-nowrap border-b border-gray-700';

function FunnelTable({
  title,
  badgeClass,
  allUsers,
  incompleteUsers,
  steps,
  searchActive,
}: {
  title: string;
  badgeClass: string;
  allUsers: AdminListUser[];
  incompleteUsers: AdminListUser[];
  steps: StepCol[];
  searchActive: boolean;
}) {
  const total = allUsers.length;
  const counts = steps.map(step => allUsers.filter(step.done).length);
  const smsVerified = allUsers.filter(u => u.smsStatus === 'Verified').length;

  return (
    <section>
      <div className="mb-3 flex items-center justify-between gap-3">
        <h3 className="text-lg font-semibold text-white">{title}</h3>
        <span className={`px-3 py-1 rounded-full text-sm ${badgeClass}`}>
          {incompleteUsers.length} incomplete
          {total > 0 ? ` · ${total} captured` : ''}
        </span>
      </div>
      {incompleteUsers.length === 0 ? (
        <p className="text-gray-500 text-sm py-6 text-center">
          {searchActive
            ? `No ${title.toLowerCase()} match your search`
            : `No incomplete ${title.toLowerCase()}`}
        </p>
      ) : (
        <div className="incomplete-funnel-scroll max-h-[70vh] w-full max-w-full overflow-auto rounded-lg border border-gray-700">
          <table className="w-max min-w-full border-separate border-spacing-0">
            <thead>
              <tr>
                <th className={`${thClass} left-0 z-30 text-left`}>Name / email</th>
                <th className={`${thClass} text-left`}>Type</th>
                <th className={`${thClass} text-left`}>Country</th>
                <th className={`${thClass} text-left`}>Plan</th>
                <th className={`${thClass} text-left`}>How they found us</th>
                <th className={`${thClass} text-left`}>Code / Paid / Abandoned</th>
                <th className={`${thClass} text-left`}>Code name</th>
                {steps.map((step, i) => (
                  <th key={step.key} className={`${thClass} text-center`}>
                    <div>{step.label}</div>
                    <div className="normal-case tracking-normal font-normal text-emerald-400/90 mt-0.5">
                      {counts[i]}
                      {total > 0 ? ` · ${Math.round((counts[i] / total) * 100)}%` : ''}
                    </div>
                  </th>
                ))}
                <th className={`${thClass} text-left`}>
                  SMS
                  <div className="normal-case tracking-normal font-normal text-emerald-400/90 mt-0.5">
                    {smsVerified}
                    {total > 0 ? ` · ${Math.round((smsVerified / total) * 100)}%` : ''} verified
                  </div>
                </th>
                <th className={`${thClass} text-left`}>Created</th>
              </tr>
            </thead>
            <tbody>
              {incompleteUsers.map(user => (
                <tr key={user.id} className="bg-gray-900">
                  <td className={`${tdClass} sticky left-0 z-10 bg-gray-900 min-w-[180px]`}>
                    <div className="text-sm text-white font-medium truncate max-w-[220px]">
                      {user.companyName || user.name || 'N/A'}
                    </div>
                    <div className="text-[11px] text-gray-400 truncate max-w-[220px]">
                      {user.email}
                    </div>
                  </td>
                  <td className={tdClass}>{user.userType}</td>
                  <td className={tdClass}>{user.country || '—'}</td>
                  <td className={tdClass}>{user.plan}</td>
                  <td className={tdClass}>{user.hearAboutUs}</td>
                  <td className={`${tdClass} ${funnelClass(user.funnelStatus)}`}>
                    {user.funnelStatus}
                  </td>
                  <td className={tdClass}>{user.codeName}</td>
                  {steps.map(step => (
                    <td key={step.key} className={`${tdClass} text-center`}>
                      <Check done={step.done(user)} />
                    </td>
                  ))}
                  <td className={tdClass}>
                    <span className="inline-flex items-center gap-1">
                      <Check done={user.smsStatus === 'Verified'} />
                      {user.smsStatus !== '—' ? user.smsStatus : null}
                    </span>
                  </td>
                  <td className={tdClass}>{formatDate(user.createdAt, '—')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function matchesUserSearch(user: AdminListUser, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return [user.name, user.email, user.companyName, user.country, user.codeName, user.id].some(
    value => (value || '').toLowerCase().includes(q)
  );
}

const IncompleteProfilesTables: React.FC<{ users: AdminListUser[]; searchQuery?: string }> = ({
  users,
  searchQuery = '',
}) => {
  const allInvestors = users.filter(u => u.userType === 'investor');
  const allStartups = users.filter(u => u.userType === 'startup');
  const incompleteInvestors = allInvestors.filter(
    u => (!u.profileExists || !u.profileCompleted) && matchesUserSearch(u, searchQuery)
  );
  const incompleteStartups = allStartups.filter(
    u => (!u.profileExists || !u.profileCompleted) && matchesUserSearch(u, searchQuery)
  );

  return (
    <div className="min-w-0 space-y-8">
      <style>{`
        .incomplete-funnel-scroll {
          scrollbar-gutter: stable;
          scrollbar-width: auto;
          scrollbar-color: #9ca3af #1f2937;
        }
        .incomplete-funnel-scroll::-webkit-scrollbar {
          width: 12px;
          height: 12px;
        }
        .incomplete-funnel-scroll::-webkit-scrollbar-track {
          background: #1f2937;
        }
        .incomplete-funnel-scroll::-webkit-scrollbar-thumb {
          background: #9ca3af;
          border-radius: 6px;
        }
        .incomplete-funnel-scroll::-webkit-scrollbar-corner {
          background: #1f2937;
        }
      `}</style>
      <p className="text-xs text-gray-500">
        Header counts are of everyone we captured (including completed profiles), so drop-off is
        visible. Rows are incomplete only. ✓ means that step is done. SMS shows Verified, Tried, or
        Send failed.
      </p>
      <FunnelTable
        title="Investors"
        badgeClass="bg-blue-500/20 text-blue-200"
        allUsers={allInvestors}
        incompleteUsers={incompleteInvestors}
        steps={INVESTOR_STEPS}
        searchActive={searchQuery.trim().length > 0}
      />
      <FunnelTable
        title="Startups"
        badgeClass="bg-yellow-500/20 text-yellow-200"
        allUsers={allStartups}
        incompleteUsers={incompleteStartups}
        steps={STARTUP_STEPS}
        searchActive={searchQuery.trim().length > 0}
      />
    </div>
  );
};

export default IncompleteProfilesTables;
